import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './s.module.less';
import { EMessageSenderType, EWSAction, TServerMessage, TWSMessage } from 'types/inbox';
import { ClockCircleOutlined } from '@ant-design/icons';
import { getTimeStr } from 'utils/calendar';
import dayjs from 'dayjs';
import InboxMessageList from './components/InboxMessageList';
import AiTextEditor from 'components/AI/AiTextEditor';
import { EAIEditorType } from 'types/ai';
import useMyAllProspectList from 'hooks/useMyAllProspectList';
import ProsepectInfo from 'pages/dashboard/ProspectsDetailPage/components/prosepectInfo';
import { handleServerSideMessage, unitAndSortServerMessages } from 'utils/inbox';
import EmptyIcon from './imgs/empty.svg';
import { Button, message, Select, Spin } from 'antd';
import { Prospect } from 'types/common';
import { sendMessage, getConversationMessageList, TGetConversationMessageList, markReadForConversation } from 'api/inbox';
import { getUserId } from 'utils/localstore';
import useInboxConversations from 'hooks/inbox/useInboxConversations';
import useInboxAllUnreadCount from 'hooks/inbox/useInboxAllUnreadCount';
import { TWebSocketListener, useWebSocket } from 'hooks/inbox/useWebSocket';
import { VALID_DOMAIN } from 'constants/common';
import { getUrlParam } from 'utils/common';
import useProspectInfo from 'hooks/useProspectInfo';

type Props = {
    clickSendMsgFlag?: {};
};

const DEFAULT_FETCH_MESSAGE_COUNT = 30;
const DEFAULT_FETCH_CONVERSATION_COUNT = 50;
const LOAD_HISTORY_INTERVAL = 1000 * 2;

const MessageContainer = (props: Props) => {
    const { clickSendMsgFlag } = props;
    const navigate = useNavigate();
    // const [get, set] = InboxStore.useStore();

    const conversationMessageMap = React.useRef<Record<string, TServerMessage[] | undefined>>({});
    const allConversationMessageFetchedFlag = React.useRef<Record<string, boolean>>({});
    const latestConversationMessageCursor = React.useRef<Record<string, string>>({});
    const latestLoadHistoryTime = React.useRef<number>(new Date().getTime());
    const [hasFocusOnDefaultConversation, setHasFocusOnDefaultConversation] = useState<boolean>(false);

    const [selectConversationId, setSelectConversationId] = useState<string>();
    const selectConversationIdRef = React.useRef<string | undefined>(selectConversationId);

    const fetchConversationSize = useRef<number>(DEFAULT_FETCH_CONVERSATION_COUNT);

    const urlClientId = useRef<string | undefined>(getUrlParam('clientId') || undefined);

    const [conversation, conversationLoading, refetchConversation] = useInboxConversations(fetchConversationSize.current, urlClientId.current);
    const [allUnreadCount, unreadLoading, refetchAllUnreadCount] = useInboxAllUnreadCount();

    const [showUnreadConversationOnly, setShowUnreadConversationOnly] = useState<boolean>(false);
    const [loadingConversationMessage, setLoadingConversationMessage] = useState<boolean>(false);

    const [showClientInfo, setShowClientInfo] = useState<boolean>(false);
    const loadingHistoryMessageRef = useRef<boolean>(false);
    const [, forceUpdate] = useState<{}>();

    const [messageListUnreadCount, setMessageListUnreadCount] = useState<number>(0);

    const autoFetchConversationInterval = React.useRef<NodeJS.Timer>();

    const hasMoreConversation = !!(conversation && conversation.length === fetchConversationSize.current);

    const targetConversation = conversation?.find((item) => item.conversationId === selectConversationId);
    const conversationId = selectConversationId || selectConversationIdRef.current;
    const [forceResetInfo, setForceResetInfo] = useState<{}>({});

    let patientId: number | undefined;
    if (conversationId) {
        const conversationIdArr = conversationId.split('_');
        if (conversationIdArr.length === 2) {
            try {
                patientId = parseInt(conversationIdArr[1], 10);
            } catch (error) {
                console.error(error);
            }
        }
    }

    const [targetClientDetail, loadingTargetClientDetail, runTargetClientDetail] = useProspectInfo(
        (patientId && showClientInfo) ? patientId : undefined,
    );

    const resetAutoFetchInterval = () => {
        if (autoFetchConversationInterval.current) {
            clearInterval(autoFetchConversationInterval.current);
        }
        autoFetchConversationInterval.current = setInterval(() => {
            refetchConversation(fetchConversationSize.current, urlClientId.current);
            refetchAllUnreadCount();
        }, 1000 * 8); // 5 s
    };
    const socketListeners = React.useRef<TWebSocketListener>({
        onOpen: (event) => {
            console.log('WebSocket connection opened:', event);
            // Optionally, you can send a message or perform an action when the connection opens
            // socketRef.current.send(JSON.stringify({ type: 'subscribe', channel: 'inbox' }));
            resetAutoFetchInterval();
            refetchConversation(fetchConversationSize.current, urlClientId.current);
            refetchAllUnreadCount();
        },
        onClose: (event) => {
            console.warn('WebSocket connection closed:', event);
            // Optionally, you can try to reconnect here
            // socketRef.current = new WebSocket(WEBSOCKET_URL);
        },
        onMessage: (event) => {
            console.info('WebSocket onMessage:', event);
            const msgData = event.data;
            try {
                const wsMsg: TWSMessage = JSON.parse(msgData);
                if (wsMsg.action === EWSAction.NEWMSG) {
                    const serverMsg = wsMsg.payload as TServerMessage;
                    const conversationIdFromWs = serverMsg.conversationId;
                    const cacheMessageList = conversationMessageMap.current[conversationIdFromWs] || [];
                    let _message = unitAndSortServerMessages([...(cacheMessageList || []), serverMsg]);

                    const currentSortId = serverMsg.sortKey;
                    const targetIndex = cacheMessageList.findIndex((item) => item.sortKey === currentSortId);
                    if (targetIndex >= 0) {
                        cacheMessageList[targetIndex] = { ...serverMsg };
                        _message = unitAndSortServerMessages(cacheMessageList);
                    }

                    conversationMessageMap.current[conversationIdFromWs] = _message || [];
                    // console.log({
                    //     _message,
                    // })
                    if (selectConversationIdRef.current === conversationIdFromWs) {
                        setMessageList(_message);
                        scrollToLatestMessage(undefined, true);
                    }
                } else {
                    console.warn('Unknown WebSocket action:', msgData);
                }
            } catch (e) {
                console.error(e);
            }
        },
        onError: (event) => {
            console.error('WebSocket error:', event);
        },
        autoCloseTime: 1000 * 60 * 10, // 10 minutes
        autoReconnectTime: 1000 * 20, // 20 seconds
    });

    const [socketRef] = useWebSocket(socketListeners.current);

    const [messageList, setMessageList] = useState<TServerMessage[]>();
    const [replayContent, setReplayContent] = useState<string>('');
    const [resetFlag, setResetFlag] = useState<{}>({});
    const [allClient, loading, run] = useMyAllProspectList();

    const [newMsgClientId, setNewMsgClientId] = useState<number>();
    const [newMsgContent, setNewMsgContent] = useState<string>('');
    const [showNewMsgModal, setShowNewMsgModal] = useState<boolean>(false);
    const [sendingNewMsg, setSendingNewMsg] = useState<boolean>(false);

    const [replyingMsg, setReplyingMsg] = useState<boolean>(false); //main pannel

    const inputWidth = window.document.body.clientWidth - (showClientInfo ? 250 : 0) - 320 - 255;

    const providerId = getUserId();

    const targetClient = allClient.find((item) => item.id === patientId);

    let conversationLabelText = '';
    if (targetConversation && targetConversation.patientFirstName) {
        conversationLabelText += targetConversation.patientFirstName.substring(0, 1);
    }
    if (targetConversation && targetConversation.patientLastName) {
        conversationLabelText += targetConversation.patientLastName.substring(0, 1);
    }
    const isEmpty = !conversation || conversation?.length === 0;
    const clientOption = allClient?.filter((item) => !item.isSample);

    const displayMessage = handleServerSideMessage(messageList, messageListUnreadCount);
    const displayConversation = conversation?.filter((item) => {
        if (item.conversationId === selectConversationId) {
            return true;
        }
        if (showUnreadConversationOnly) {
            return item.unreadCount && item.unreadCount > 0;
        }
        return true;
    });

    const hasUnreadMessageAll = !!(allUnreadCount && allUnreadCount > 0);

    React.useEffect(() => {
        if (patientId && showClientInfo) {

        }
    }, [patientId, showClientInfo]);

    React.useEffect(() => {
        if (hasFocusOnDefaultConversation || !urlClientId.current) {
            return;
        }
        const targetCid = `${getUserId()}_${urlClientId.current}`;
        const targetConversationFromUrl = conversation?.find((item) => item.conversationId === targetCid);
        if (targetConversationFromUrl) {
            setHasFocusOnDefaultConversation(true);
            setSelectConversationId(targetCid);
            selectConversationIdRef.current = targetCid;
        }
    }, [conversation, hasFocusOnDefaultConversation]);

    const handleShowMsgModal = () => {
        setShowNewMsgModal(true);
        setNewMsgClientId(undefined);
        setNewMsgContent('');
        setResetFlag({});
    };

    const scrollToLatestMessage = (timeout?: number, smooth?: boolean) => {
        setTimeout(() => {
            const messageWrap = document.getElementById('messageWrap');
            if (messageWrap) {
                messageWrap.scrollTo({
                    top: 99999,
                    behavior: smooth ? 'smooth' : 'auto',
                });
            }
        }, timeout || 150);
    };

    const checkContent = (content: string): boolean => {
        const regex = /https?:\/\/[^\s，,；;\]\)\(\[\“\"\'<>]+/g;
        const links = content.match(regex);
        let isValid = true;
        links?.forEach((link) => {
            try {
                const url = new URL(link);
                if (url.hostname) {
                    let hostIsValid = false;
                    VALID_DOMAIN.forEach((domain) => {
                        if (url.hostname.endsWith(domain)) {
                            hostIsValid = true;
                        }
                    });
                    if (!hostIsValid) {
                        isValid = false;
                    }
                }
            } catch (e) {
                console.error(e);
            }
        });
        return isValid;
    };

    const onClickSend = async () => {
        if (!newMsgClientId || !newMsgContent || !providerId) {
            message.error('Please select a client and enter a message.');
            return;
        }

        // if (!checkContent((newMsgContent))) {
        //     message.error('Invalid content! Please check the links in your message.');
        //     return;
        // }

        setSendingNewMsg(true);
        const res = await sendMessage({
            providerId: providerId!,
            clientId: newMsgClientId,
            message: newMsgContent.replace(/<[^>]+>/g, ''),
        });
        setSendingNewMsg(false);
        if (!res.error) {
            message.success('Sent successfully!');
            setShowNewMsgModal(false);
            setNewMsgClientId(undefined);
            setNewMsgContent('');
            setResetFlag({});
            resetAutoFetchInterval();
            refetchConversation(fetchConversationSize.current, urlClientId.current);
            refetchAllUnreadCount();
            //todo update conversation
        }
    };

    const onClickReply = async () => {
        // if (!checkContent((replayContent))) {
        //     message.error('Invalid content! Please check the links in your message.');
        //     return;
        // }

        if (!targetClient?.id) {
            run();
        }

        setReplyingMsg(true);
        const res = await sendMessage({
            providerId: providerId!,
            clientId: targetClient?.id! || patientId!,
            message: replayContent.replace(/<[^>]+>/g, ''),
        });
        setReplyingMsg(false);

        if (!res.error) {
            setReplayContent('');
            setResetFlag({});
            resetAutoFetchInterval();
            setTimeout(() => {
                refetchConversation(fetchConversationSize.current, urlClientId.current);
                refetchAllUnreadCount();
            }, 2000);

            //not need
            //fetchMessageByConversationId(selectConversationId!, undefined, 5);
            //add local message
            const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
            const messageData: TServerMessage = res.data?.data;

            //{\"body\":\"Thank you for reaching out. I am Evan, your healthcare provider. How can I assist you today?\"}
            // const addMessage: TServerMessage = {
            //     senderId: providerId!,
            //     messageId: `${dayjs().unix()}_${Math.random().toString(36).substring(2, 15)}`,
            //     messageType: EMessageType.TEXT,
            //     conversationId: selectConversationId!,
            //     receiverId: targetClient?.id!,
            //     receiverType: EMessageSenderType.PATIENT,
            //     replyToMessageId: undefined,
            //     visibleTo: [EMessageSenderType.PATIENT, EMessageSenderType.PROVIDER],
            //     content: JSON.stringify({
            //         body: replayContent,
            //     }),
            //     senderType: EMessageSenderType.PROVIDER,
            //     createdAt: dayjs().toISOString(),
            //     aiReplyContent: undefined, // AI reply content is not applicable here,
            //     updatedAt: dayjs().toISOString(),
            //     sortKey: `${dayjs().toISOString()}#${Math.random().toString(36).substring(2, 15)}`,
            // }
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
            conversationMessageMap.current[selectConversationId!] = _message || [];
            setMessageList(_message);
            scrollToLatestMessage(100, true);
            //end add local message
        }
    };

    const onClickDiscard = async () => {
        setNewMsgClientId(undefined);
        setNewMsgContent('');
        setResetFlag({});
    };

    const handleChangeConversation = async (cid: string) => {
        urlClientId.current = undefined;
        const cacheMessage = conversationMessageMap.current[cid] || [];
        setMessageList(cacheMessage);
        setSelectConversationId(cid);
        const _targetConversation = conversation?.find((item) => item.conversationId === cid);
        if (_targetConversation) {
            setMessageListUnreadCount(_targetConversation.unreadCount || 0);
        }
        const res = await markReadForConversation(cid);
        if (selectConversationId) {
            const res1 = await markReadForConversation(selectConversationId);
        }
        if (!res.error) {
            resetAutoFetchInterval();
            refetchConversation(fetchConversationSize.current, urlClientId.current);
            refetchAllUnreadCount();
        }

        // setForceResetInfo({});
    };

    const fetchMessageByConversationId = async (_cid: string, cursor?: string, size?: number) => {
        const param: TGetConversationMessageList = {
            conversationId: _cid,
            size: size || DEFAULT_FETCH_MESSAGE_COUNT,
        };
        if (cursor) {
            param.cursor = cursor;
        }
        setLoadingConversationMessage(true);
        const res = await getConversationMessageList(param);
        if (!res.error) {
            const data = res?.data?.data?.items || [];
            const _isEmpty = data.length === 0;
            const _cursor = res?.data?.data?.cursor;
            const currentCursor = latestConversationMessageCursor.current[_cid] || '';
            if (!currentCursor || cursor) {
                //mean loading history or loading new but with any cursor
                latestConversationMessageCursor.current[_cid] = _cursor || '';
            }

            if (_isEmpty || !_cursor) {
                allConversationMessageFetchedFlag.current[_cid] = true;
            }

            const cacheMessageList = conversationMessageMap.current[_cid] || [];
            const _message = unitAndSortServerMessages([...(cacheMessageList || []), ...data]);
            conversationMessageMap.current[_cid] = _message || [];
            if (_message && _message[_message.length - 1] && _message[_message.length - 1].aiReplyContent) {
                //last message
                setReplayContent(_message[_message.length - 1].aiReplyContent!);
            }
            setMessageList(_message);
            if (!cursor) {
                scrollToLatestMessage();
            }
        }
        setLoadingConversationMessage(false);
    };

    const loadHistoryMessage = async () => {
        if (loadingHistoryMessageRef.current === true) {
            return;
        }
        const targetConversationId = selectConversationIdRef.current;
        if (!targetConversationId) {
            return;
        }
        const cursor = latestConversationMessageCursor.current[targetConversationId] || '';
        if (!cursor) {
            return;
        }

        const allLoad = allConversationMessageFetchedFlag.current[targetConversationId];
        if (allLoad) {
            return;
        }
        const nowTime = new Date().getTime();
        const latestLoadTime = latestLoadHistoryTime.current;
        const diff = nowTime - latestLoadTime;
        if (diff < LOAD_HISTORY_INTERVAL) {
            return;
        }
        latestLoadHistoryTime.current = nowTime;
        loadingHistoryMessageRef.current = true;
        forceUpdate({});
        await fetchMessageByConversationId(targetConversationId, cursor, DEFAULT_FETCH_MESSAGE_COUNT);
        loadingHistoryMessageRef.current = false;
        forceUpdate({});
    };
    React.useEffect(() => {
        if (isEmpty) {
            return;
        }
        const messageScrollCheck = (e: Event) => {
            const target = e.target as HTMLElement;
            const { scrollTop } = target;
            if (scrollTop === 0) {
                loadHistoryMessage();
            }
        };

        const messageWrap = document.getElementById('messageWrap');
        if (messageWrap) {
            messageWrap.addEventListener('scroll', messageScrollCheck);
        }
        return () => {
            if (messageWrap) {
                messageWrap.removeEventListener('scroll', messageScrollCheck);
            }
        };
    }, [isEmpty]);

    React.useEffect(() => {
        return () => {
            if (socketRef) {
                socketRef.close();
            }
        };
    }, []);

    React.useEffect(() => {
        resetAutoFetchInterval();
        return () => {
            if (autoFetchConversationInterval.current) {
                clearInterval(autoFetchConversationInterval.current);
            }
        };
    }, []);

    React.useEffect(() => {
        if (conversation && conversation.length > 0 && !selectConversationId && !selectConversationIdRef.current) {
            setSelectConversationId(conversation[0].conversationId);
            setMessageListUnreadCount(conversation[0].unreadCount || 0);
        }
    }, [conversation]);

    React.useEffect(() => {
        //for use in ws
        selectConversationIdRef.current = selectConversationId;
        if (selectConversationId) {
            fetchMessageByConversationId(selectConversationId);
        }
    }, [selectConversationId]);

    React.useEffect(() => {
        if (clickSendMsgFlag) {
            handleShowMsgModal();
        }
    }, [clickSendMsgFlag]);

    return (
        <div className={`${s.wrap} ${isEmpty ? s.emptyWrap : ''}`}>
            <div className={s.left}>
                <div className={s.title}>
                    Message
                </div>
                <div
                    onClick={() => {
                        if (!hasUnreadMessageAll && !showUnreadConversationOnly) {
                            return;
                        }
                        setShowUnreadConversationOnly(!showUnreadConversationOnly);
                    }}
                    className={`${s.unReadLabel} ${hasUnreadMessageAll ? s.unReadLabelEnable : ''} ${showUnreadConversationOnly ? s.unReadLabelSelect : ''}`}
                >
                    Unread ({allUnreadCount || 0})
                </div>
                <div className={s.list}>
                    {displayConversation?.map((item) => {
                        const hasUnread = !!(item.unreadCount && item.unreadCount > 0);
                        const updateTime = dayjs(item.lastMessageAt);
                        const isToday = updateTime.isSame(dayjs(), 'day');
                        let name = `${item.patientFirstName} ${item.patientLastName}`.trim();
                        if (item.lastMessageSenderType === EMessageSenderType.PROVIDER) {
                            name = 'You';
                        } else if (item.lastMessageSenderType === EMessageSenderType.SYSTEM) {
                            name = 'System';
                        }
                        return (
                            <div
                                className={`${s.item} ${hasUnread ? s.itemUnread : ''} ${selectConversationId === item.conversationId ? s.itemSelect : ''}`}
                                key={item.conversationId}
                                onClick={() => {
                                    handleChangeConversation(item.conversationId);
                                }}
                            >
                                <div className={s.itemHeader}>
                                    <span className={s.name}>{item.patientFirstName} {item.patientLastName}</span>
                                    {
                                        hasUnread && <span className={s.unReadCount}>{item.unreadCount}</span>
                                    }
                                </div>
                                <div className={s.content}>
                                    {name}: {item.lastMessageContent}
                                </div>
                                <div className={s.timeRow}>
                                    <ClockCircleOutlined style={{ color: '#BDBDBD', marginRight: 4 }} />
                                    <span className={s.time}>{isToday ? 'Today, ' : ''} {getTimeStr(dayjs(item.lastMessageAt), 'MMM DD, hh:mm A')}</span>
                                </div>
                            </div>
                        );
                    })}
                    {
                        hasMoreConversation && (
                            <div
                                className={s.viewMore}
                                onClick={() => {
                                    fetchConversationSize.current = 1000;
                                }}
                            >
                                <Button block>View more</Button>
                            </div>
                        )
                    }
                </div>
            </div>
            <div className={s.mid}>
                {
                    !isEmpty && conversation !== undefined &&
                    <>
                        <div
                            onClick={() => {
                                setShowClientInfo(!showClientInfo);
                            }}
                            className={s.cName}
                        >
                            <span className={s.icon}>{conversationLabelText}</span>
                            {targetConversation?.patientFirstName} {targetConversation?.patientLastName}
                        </div>
                        <div className={s.messageWrap} id="messageWrap">
                            {loadingHistoryMessageRef.current && <Spin className={s.messageLoadingHistory} />}
                            <InboxMessageList inboxMessageList={displayMessage} inboxConversation={targetConversation} />
                            {loadingConversationMessage && !loadingHistoryMessageRef.current && <Spin className={s.messageLoading} />}
                        </div>
                        <div className={s.messageInput}>
                            <div className={s.aiWrap}>
                                <AiTextEditor
                                    rows={6}
                                    onChange={(e) => {
                                        if (typeof e === 'string') {
                                            setReplayContent(e as string);
                                        } else {
                                            setReplayContent(e ? e.target.value : '');
                                        }
                                    }}
                                    value={replayContent}
                                    type={EAIEditorType.PROSPECT}
                                    resetFlag={resetFlag}
                                    maxLength={3000}
                                    narrow={inputWidth < 750}
                                />
                            </div>

                            <div className={s.btnWrap}>
                                <Button
                                    className={s.btn}
                                    loading={replyingMsg}
                                    onClick={() => {
                                        onClickReply();
                                    }}
                                    type="primary"
                                    disabled={!replayContent}
                                >Send
                                </Button>
                            </div>
                        </div>
                    </>
                }
                {
                    isEmpty && conversation !== undefined && (
                        <div className={s.emptyWrap}>
                            <img src={EmptyIcon} />
                            <div className={s.emptyTitle}>No messages yet</div>
                            <div className={s.emptyDes}>
                                Appointment updates, system notifications, and client messages will show up here.
                                Start a conversation with your clients anytime!
                            </div>
                            <Button
                                onClick={handleShowMsgModal}
                                type="primary"
                            >Send message
                            </Button>
                        </div>
                    )
                }

            </div>
            {
                showClientInfo && targetClient && !isEmpty && (
                    <div className={s.right}>
                        <div className={s.pTitle}>
                            <span>Client details</span>
                            <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setShowClientInfo(false);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                                    <path d="M6 18.5L18 6.5M6 6.5L18 18.5" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </div>
                        <div className={s.pWrap}>
                            <ProsepectInfo
                                forceResetInfo={forceResetInfo}
                                showMoreInfo
                                smallSize
                                data={loadingTargetClientDetail ? undefined : targetClientDetail}
                                onUpdate={() => {
                                    run();
                                }}
                            />
                        </div>
                    </div>
                )
            }
            <div className={`${s.newMsgWrap} ${showNewMsgModal ? s.newMsgWrapShow : ''}`}>
                <div className={s.nTitle}>
                    <span>New message</span>
                    <svg
                        onClick={() => {
                            setNewMsgClientId(undefined);
                            setNewMsgContent('');
                            setResetFlag({});
                            setShowNewMsgModal(false);
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path d="M6 18L18 6M6 6L18 18" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className={s.nContent}>
                    <div className={s.nLabel}>Client</div>
                    <Select
                        showSearch
                        className={s.select}
                        onChange={(val) => {
                            setNewMsgClientId(val);
                        }}
                        filterOption={(input, option) => {
                            const name = option?.children?.join('');
                            if (!name) return true;
                            return name?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
                        }}
                        value={newMsgClientId}
                        placeholder="Select a client"
                        style={{ width: '200px' }}
                    >
                        {
                            clientOption?.map((item: Prospect) => {
                                return (
                                    <Select.Option value={item.id} key={item.id}>
                                        {item.firstName} {item.lastName}
                                    </Select.Option>
                                );
                            })
                        }
                    </Select>
                    <div className={s.nLabel}>Message</div>
                    <div className={s.inputWrap}>
                        <AiTextEditor
                            size="small"
                            rows={6}
                            maxLength={3000}
                            onChange={(e) => {
                                if (typeof e === 'string') {
                                    setNewMsgContent(e as string);
                                } else {
                                    setNewMsgContent(e ? e.target.value : '');
                                }
                            }}
                            value={newMsgContent}
                            type={EAIEditorType.PROSPECT}
                            resetFlag={resetFlag}
                            placeholder=""
                        />
                    </div>
                </div>
                <div className={s.nFooter}>
                    <Button
                        style={{ marginRight: '16px' }}
                        onClick={onClickDiscard}
                    >
                        Discard
                    </Button>
                    <Button
                        loading={sendingNewMsg}
                        disabled={!newMsgClientId || !newMsgContent}
                        type="primary"
                        onClick={onClickSend}
                    >
                        Send
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MessageContainer;
